import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { KibChevronRightIcon } from '@chewy/kib-icons-react'
import classNames from 'classnames'
import type { Constant } from '@pbt/pbt-ui-components'

import BusinessShareIcon from '~/components/common/icons/BusinessShareIcon'
import ColorPinIcon from '~/components/common/icons/ColorPinIcon'
import { ControlledSubstanceChip } from '~/components/common/labels/ControlledSubstanceChip'
import RxStatusChip from '~/components/common/labels/RxStatusChip'
import { setTimelineItem } from '~/store/actions/timeline'
import { getPrescriptionStates } from '~/store/reducers/constants'
import type { PrescriptionTimelineItem } from '~/types'

import { getPrescriptionTitle } from './prescriptions/rxTracker/rxCardUtilities'
import { useDoctorInfoDisplay } from './prescriptions/rxTracker/useDoctorInfoDisplay'

import styles from './RxCardHeader.module.scss'

export interface RxCardHeaderProps {
  actionNeeded?: boolean
  dateTitle?: React.ReactNode
  onClick?: () => void
  subheader?: React.ReactNode
  timelineItem?: PrescriptionTimelineItem
  titlePinColor?: string | null
}

const RxCardHeader: React.FC<RxCardHeaderProps> = ({
  actionNeeded = false,
  dateTitle,
  onClick,
  timelineItem,
  subheader,
  titlePinColor,
}) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const prescriptionId = timelineItem?.prescription.id
  const prescriptionDetailsPath = `/prescription/${prescriptionId}`
  const isClickable =
    Boolean(onClick) ||
    (Boolean(prescriptionId) && location.pathname !== prescriptionDetailsPath)

  const controlled = timelineItem?.controlled ?? false
  const businessId = timelineItem?.businessId ?? null

  const prescriptionStates = useSelector(getPrescriptionStates) as
    | Constant[]
    | undefined

  const dateLabel = timelineItem ? (
    <RxStatusChip
      prescription={timelineItem.prescription}
      prescriptionStates={prescriptionStates}
    />
  ) : null

  const doctorInfo = useDoctorInfoDisplay({
    doctor: timelineItem?.prescription.doctor ?? timelineItem?.vet ?? null,
    signerId: timelineItem?.signerId ?? null,
    stateId: timelineItem?.prescription.stateId ?? null,
    prescription: timelineItem?.prescription
      ? {
          id: timelineItem.prescription.id,
          createdAt: timelineItem.prescription.createdAt,
          creationDate: timelineItem.prescription.creationDate,
          modificationDate: timelineItem.prescription.modificationDate,
          updatedAt: timelineItem.prescription.updatedAt,
        }
      : undefined,
  })

  const title = timelineItem
    ? getPrescriptionTitle(timelineItem.prescription, timelineItem, false)
    : null

  const onActivate = () => {
    if (onClick) {
      onClick()
      return
    }

    if (prescriptionId && timelineItem) {
      dispatch(setTimelineItem(timelineItem))
      navigate(prescriptionDetailsPath)
    }
  }

  const hasDateInfo = Boolean(dateTitle) || Boolean(subheader)

  const header = (
    <div
      className={classNames(styles.root, {
        [styles.actionNeeded]: actionNeeded,
      })}
    >
      {hasDateInfo && (
        <div className={styles.headerRoot}>
          {dateTitle && <span className={styles.dateTitle}>{dateTitle}</span>}
          {subheader && (
            <span className={styles.headerMeta}>
              <span className={styles.dateSubheader}>{subheader}</span>
            </span>
          )}
        </div>
      )}

      <div className={styles.doctorInfo}>
        {dateLabel && <span className={styles.dateLabel}>{dateLabel}</span>}
        {doctorInfo}
      </div>

      <div className={styles.titleRow}>
        <ColorPinIcon
          alignSelf="center"
          color={titlePinColor ?? null}
          variant="big"
        />
        {title && (
          <span className={styles.titleContainer}>
            {React.isValidElement(title) ? (
              title
            ) : (
              <span className={styles.title}>{title}</span>
            )}
            {controlled && (
              <span className={styles.controlledSubstanceChip}>
                <ControlledSubstanceChip />
              </span>
            )}
          </span>
        )}
        <span className={styles.actions}>
          {isClickable && <KibChevronRightIcon className={styles.chevron} />}
          {businessId && <BusinessShareIcon businessIds={[businessId]} />}
        </span>
      </div>
    </div>
  )

  return isClickable ? (
    <button
      className={styles.clickableHeader}
      type="button"
      onClick={onActivate}
    >
      {header}
    </button>
  ) : (
    header
  )
}

export default RxCardHeader
